import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, type OnDestroy } from '@angular/core'
import { MatCalendar } from '@angular/material/datepicker'
import {
  DateAdapter, MAT_DATE_FORMATS, MatDateFormats
} from '@angular/material/core'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import * as moment from 'moment'
import { NGXLogger } from 'ngx-logger'

import { CommonModule } from '@angular/common'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { RouterModule } from '@angular/router'
import { CustomerPortalSharedModule } from '@inside-hub-app/customer-portal-shared'
import { HubChildTranslateModule } from '@inside-hub-app/hub-translate'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { NgxGlideModule } from 'ngx-glide'
import { GoogleMapsModule } from '@angular/google-maps'
import { A11yModule } from '@angular/cdk/a11y'
import { TextFieldModule } from '@angular/cdk/text-field'
@Component({
  selector: 'customer-portal-app-custom-calendar-header',
  standalone: true,
  styles: [
        `
      .example-header {
        display: flex;
        align-items: center;
        padding: 0.5em;
      }

      .example-header button:disabled {
        color: grey;
        background: transparent;
        border: none;
      }

      .example-header-label {
        flex: 1;
        height: 1.3em;
        font-weight: 500;
        text-align: center;
        display: grid;
        grid-template-columns: 1fr auto;
        cursor: pointer;
      }

      .example-double-arrow .mat-icon {
        margin: -22%;
      }
      .example-header {
        color: #003899;
        height: 48px;
        font-size: 20px;
      }
    `
  ],
  template: `
    <div class="example-header" (load)="(periodLabel)">
      <button
        class="CustomCalendarHeaderComponent-previous-button"
        [disabled]="disableLast"
        mat-icon-button
        (click)="previousClicked()"
      >
        <mat-icon svgIcon="chevron_left"></mat-icon>
      </button>
      <span class="example-header-label" (click)="yearChange()"
        >{{ periodLabel
        }}<mat-icon class="icon" svgIcon="arrow_drop_down"></mat-icon
      ></span>
      <button
        class="CustomCalendarHeaderComponent-next-button"
        [disabled]="disableNext"
        mat-icon-button
        (click)="nextClicked()"
      >
        <mat-icon svgIcon="chevron_right"></mat-icon>
      </button>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    CustomerPortalSharedModule,
    HubChildTranslateModule,
    NgxSkeletonLoaderModule,
    NgxGlideModule,
    GoogleMapsModule,
    A11yModule,
    TextFieldModule
  ]
})
export class CustomCalendarHeaderComponent<D> implements OnDestroy {
  private readonly _destroyed = new Subject<void>()
  public disableNext = false
  public disableLast = false

  yearSelectedSub
  constructor (
    private readonly _calendar: MatCalendar<D>,
    private readonly _dateAdapter: DateAdapter<D>,
    @Inject(MAT_DATE_FORMATS) private readonly _dateFormats: MatDateFormats,
    cdr: ChangeDetectorRef,
    private readonly logger: NGXLogger
  ) {
    _calendar.stateChanges
      .pipe(takeUntil(this._destroyed))
      .subscribe(() => { cdr.markForCheck() })
    this.disableArrows()

    this.yearSelectedSub = _calendar.yearSelected.subscribe(() => {
      setTimeout(() => {
        this._calendar.currentView = 'month'
      })
    })
  }

  ngOnDestroy (): void {
    this._destroyed.next()
    this._destroyed.complete()
    try {
      this.yearSelectedSub.unsubscribe()
    } catch (error) {
      // no need to show log
    }
  }

  get periodLabel (): string {
    const today = moment(new Date())
    const current = moment(this._calendar.activeDate)

    this.disableNext =
      this._calendar.maxDate != null &&
      today.month() === current.month() &&
      today.year() - 18 === current.year()

    return this._dateAdapter.format(
      this._calendar.activeDate,
      this._dateFormats.display.monthYearLabel
    )
  }

  disableArrows (): void {
    const current = moment(this._calendar.activeDate)
    this.disableLast =
      this._calendar.minDate != null &&
      moment(this._calendar.minDate).month() === current.month() &&
      moment(this._calendar.minDate).year() === current.year()
  }

  yearChange (): void {
    if (
      this._calendar.currentView !== 'multi-year' &&
      this._calendar.currentView !== 'year'
    ) {
      this._calendar.currentView = 'multi-year'
    } else {
      this._calendar.currentView = 'month'
    }
  }

  previousClicked (): void {
    const mode = this._calendar.currentView === 'month' ? 'month' : 'year'
    this._calendar.activeDate =
      mode === 'month'
        ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, -1)
        : this._dateAdapter.addCalendarYears(this._calendar.activeDate, -24)
    this.disableArrows()
    const date = this._calendar.activeDate
    this.disableLast =
      moment(date).year() - 24 <= 1900 || moment(date).year() < 1900
  }

  nextClicked (): void {
    const mode = this._calendar.currentView === 'month' ? 'month' : 'year'
    this._calendar.activeDate =
      mode === 'month'
        ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, 1)
        : this._dateAdapter.addCalendarYears(this._calendar.activeDate, 24)
    this.disableArrows()
  }
}
